/* ============================================================
   AVORA HOME — Cart Drawer
   Slide-in cart drawer with full Shopify AJAX API integration.
   ============================================================ */

(function() {
  'use strict';

  var drawer, overlay, itemsContainer, emptyState, footer;
  var countEl, subtotalEl, checkoutBtn, requestStatus, requestMessage, retryBtn, fallbackLink;
  var isOpen = false;
  var lastFocused = null;
  var mutationQueue = Promise.resolve();
  var pendingQuantities = Object.create(null);
  var mutationFocus = null;
  var statusTimer = null;
  var currentCart = null;
  var hasRenderedCart = false;
  var cartEpoch = 0;

  function rootPath(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/?$/, '/') + String(path || '').replace(/^\//, '');
  }

  function init() {
    drawer = document.getElementById('avora-cart-drawer');
    overlay = document.getElementById('avora-drawer-overlay');
    itemsContainer = document.getElementById('avora-drawer-items');
    emptyState = document.getElementById('avora-drawer-empty');
    footer = document.getElementById('avora-drawer-footer');
    countEl = document.getElementById('avora-drawer-count');
    subtotalEl = document.getElementById('avora-drawer-subtotal');
    checkoutBtn = document.getElementById('avora-drawer-checkout');
    requestStatus = document.getElementById('avora-drawer-request-status');
    requestMessage = document.getElementById('avora-drawer-request-message');
    retryBtn = document.getElementById('avora-drawer-retry');
    fallbackLink = document.getElementById('avora-drawer-cart-fallback');

    if (!drawer) return;

    // Close button
    var closeBtn = document.getElementById('avora-drawer-close');
    if (closeBtn) closeBtn.addEventListener('click', close);

    // Overlay click to close
    if (overlay) overlay.addEventListener('click', close);
    if (retryBtn) retryBtn.addEventListener('click', fetchAndRender);

    // ESC key to close
    document.addEventListener('keydown', function(e) {
      if (e.target.closest && e.target.closest('.avora-cart-confirm')) return;
      if (!isOpen) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      var focusable = Array.from(drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter(function(el) { return el.getClientRects().length && !el.closest('[hidden]'); });
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    // Intercept cart icon clicks to open drawer instead of navigating
    interceptCartIconClicks();

    // Expose globally. Rewards uses the same renderer so every cart surface stays in sync.
    window.avoraCartDrawer = {
      open: open,
      close: close,
      refresh: fetchAndRender,
      render: renderCart,
      syncCount: updateHeaderCartCount,
      cache: cacheCart,
      toast: showDrawerToast,
      whenIdle: function() { return mutationQueue; }
    };
  }

  function cacheCart(cart) {
    cartEpoch++;
    currentCart = cart;
    setRequestStatus('', false);
    // Rewards may finish its initial read while the first drawer GET is pending.
    // That supersedes the GET, so paint the confirmed cart here on first opening.
    // Subsequent reward events only cache: rendering them recursively would loop.
    if (isOpen && !hasRenderedCart) renderCart(cart);
  }

  /* ----------------------------------------------------------
     OPEN / CLOSE
     ---------------------------------------------------------- */

  function open(cart) {
    if (!drawer) return;
    lastFocused = document.activeElement;
    if (cart && typeof cart.item_count === 'number') renderCart(cart);
    else {
      if (currentCart) renderCart(currentCart);
      fetchAndRender();
    }
    drawer.removeAttribute('inert');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-open');
    document.body.classList.add('avora-drawer-open');
    isOpen = true;

    // Focus trap: focus close button
    var closeBtn = document.getElementById('avora-drawer-close');
    if (closeBtn) setTimeout(function() { if (isOpen) closeBtn.focus(); }, 100);
  }

  function close() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    document.body.classList.remove('avora-drawer-open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    isOpen = false;
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  /* ----------------------------------------------------------
     CART API
     ---------------------------------------------------------- */

  function requestCart(path, options) {
    var controller = new AbortController();
    var timeout = window.setTimeout(function() {controller.abort();},12000);
    return fetch(rootPath(path), Object.assign({credentials:'same-origin',headers:{'X-Requested-With':'XMLHttpRequest'}},options,{signal:controller.signal}))
      .then(function(response) {
        return response.json().catch(function() {return {};}).then(function(data) {
          if(!response.ok) throw new Error(data.description || data.message || window.AvoraI18n.text('Could not update your cart. Please try again.'));
          if(!Array.isArray(data.items)) throw new Error('Invalid cart response');
          return data;
        });
      }).finally(function() {window.clearTimeout(timeout);});
  }

  function fetchAndRender() {
    var request = ++cartEpoch;
    setRequestStatus(window.AvoraI18n.text('Loading your cart…'), false);
    return requestCart('cart.js')
      .then(function(cart) {
        if(request!==cartEpoch) return currentCart;
        setRequestStatus('', false);
        renderCart(cart);
        return cart;
      })
      .catch(function() {
        if(request!==cartEpoch) return currentCart;
        setRequestStatus(window.AvoraI18n.text('Your cart could not be loaded.'), true);
        showDrawerToast(window.AvoraI18n.text('Your cart could not be loaded. Retry or open the full cart page.'));
        return null;
      });
  }

  function steppedQuantity(item, quantity, direction) {
    var rule=item && item.quantity_rule || {};
    var minimum=Math.max(1,Number(rule.min)||1), increment=Math.max(1,Number(rule.increment)||1);
    if(direction<0) return Math.max(minimum,quantity-increment);
    var next=quantity+increment, maximum=Number(rule.max)||Infinity;
    return next>maximum ? quantity : next;
  }

  function setRequestStatus(message, retryable) {
    if (!requestStatus || !requestMessage) return;
    if (statusTimer) {
      window.clearTimeout(statusTimer);
      statusTimer = null;
    }
    requestStatus.classList.remove('visually-hidden');
    requestMessage.textContent = message;
    requestStatus.hidden = !message;
    if (retryBtn) retryBtn.hidden = !retryable;
    if (fallbackLink) fallbackLink.hidden = !retryable;
  }

  function changeQuantity(lineKey, quantity) {
    cartEpoch++;
    pendingQuantities[lineKey] = quantity;
    var item = itemsContainer.querySelector('[data-line-key="' + lineKey + '"]');
    if (item) { item.classList.add('is-updating'); item.setAttribute('aria-busy', 'true'); }

    var operation = mutationQueue.catch(function() {}).then(function() {
      cartEpoch++;
      return requestCart('cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: lineKey, quantity: quantity })
      }).then(function(cart) {
        if (pendingQuantities[lineKey] === quantity) delete pendingQuantities[lineKey];
        renderCart(cart);
        updateHeaderCartCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:update', {
          detail: {
            cart: cart,
            resource: cart,
            data: {
              cart: cart,
              itemCount: cart.item_count,
              source: 'avora-cart-drawer'
            }
          }
        }));
        announceCart(window.AvoraI18n.text('Cart updated. ') + cart.item_count + ' item' + (cart.item_count === 1 ? '' : 's') + '.');
        return cart;
      }).catch(function(err) {
        if (pendingQuantities[lineKey] === quantity) delete pendingQuantities[lineKey];
        var currentItem = itemsContainer && itemsContainer.querySelector('[data-line-key="' + lineKey + '"]');
        if (currentItem) {currentItem.classList.remove('is-updating');currentItem.removeAttribute('aria-busy');}
        var msg = err && err.name !== 'AbortError' && (err.description || err.message) || window.AvoraI18n.text('Could not update quantity. Please try again.');
        showDrawerToast(msg);
        return fetchAndRender().catch(function() {});
      });
    });

    mutationQueue = operation.catch(function() {});
    return operation;
  }

  function announceCart(message) {
    if (!requestMessage || !requestStatus) return;
    requestMessage.textContent = '';
    requestStatus.hidden = false;
    requestStatus.classList.add('visually-hidden');
    window.setTimeout(function() { requestMessage.textContent = message; }, 0);
    statusTimer = window.setTimeout(function() {
      requestStatus.hidden = true;
      requestStatus.classList.remove('visually-hidden');
      statusTimer = null;
    }, 1800);
  }

  /* Toast notification inside cart drawer */
  function showDrawerToast(message) {
    var existing = drawer.querySelector('.avora-drawer__toast');
    if (existing) existing.remove();

    var toast = document.createElement('div');
    toast.className = 'avora-drawer__toast';
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'assertive');
    toast.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<circle cx="12" cy="12" r="10"/>' +
        '<line x1="12" y1="8" x2="12" y2="12"/>' +
        '<line x1="12" y1="16" x2="12.01" y2="16"/>' +
      '</svg>' +
      '<span>' + escapeHTML(message) + '</span>';

    var header = drawer.querySelector('.avora-drawer__header');
    if (header && header.nextSibling) {
      header.parentNode.insertBefore(toast, header.nextSibling);
    } else {
      drawer.appendChild(toast);
    }

    setTimeout(function() {
      toast.classList.add('is-leaving');
      setTimeout(function() { toast.remove(); }, 300);
    }, 4000);
  }

  async function removeItem(lineKey) {
    var item = currentCart && currentCart.items.find(function(line) { return line.key === lineKey; });
    if (!item || !window.AvoraCartInteractions) return;
    var approved = await window.AvoraCartInteractions.confirmRemoval(lineKey,item.product_title || item.title);
    if (!approved || !currentCart.items.some(function(line) { return line.key === lineKey; })) return;
    return changeQuantity(lineKey, 0);
  }

  /* ----------------------------------------------------------
     RENDER
     ---------------------------------------------------------- */

  // Cart lines carry no compare-at price, so read it from the product JSON once
  // per product. Shopify's price is the sale price; compare-at is the original.
  var compareAtByVariant = {};
  var compareAtRequested = {};

  function compareLinePrice(item) {
    if (item.properties && item.properties._avora_reward_gift) return 0;
    var finalLine = Number(item.final_line_price) || 0;
    var compareLine = (compareAtByVariant[item.variant_id] || 0) * (Number(item.quantity) || 0);
    if (compareLine > finalLine) return compareLine;
    return Number(item.original_line_price) > finalLine ? Number(item.original_line_price) : 0;
  }

  function showCompareRows() {
    if (!itemsContainer || !currentCart) return;
    (currentCart.items || []).forEach(function(item) {
      var compare = compareLinePrice(item);
      if (!compare) return;
      var row = itemsContainer.querySelector('[data-line-key="' + CSS.escape(item.key) + '"] .avora-drawer__item-price-row');
      if (!row || row.querySelector('.avora-drawer__item-compare')) return;
      var struck = document.createElement('s');
      struck.className = 'avora-drawer__item-compare';
      struck.textContent = formatCartMoney(compare);
      row.appendChild(struck);
    });
  }

  function loadCompareAtPrices(items) {
    var handles = [];
    items.forEach(function(item) {
      if (!item.handle || compareAtRequested[item.handle] || item.variant_id in compareAtByVariant) return;
      compareAtRequested[item.handle] = true;
      handles.push(item.handle);
    });
    if (!handles.length) return;
    Promise.all(handles.map(function(handle) {
      return fetch(rootPath('products/' + handle + '.js'), {credentials:'same-origin'})
        .then(function(response) { return response.ok ? response.json() : null; })
        .then(function(product) {
          ((product && product.variants) || []).forEach(function(variant) {
            compareAtByVariant[variant.id] = Number(variant.compare_at_price) > Number(variant.price) ? Number(variant.compare_at_price) : 0;
          });
        })
        .catch(function() {});
    })).then(showCompareRows);
  }

  function renderCart(cart) {
    hasRenderedCart = true;
    cartEpoch++;
    setRequestStatus('', false);
    currentCart = cart;
    var items = (cart.items || []).slice().sort(function(a, b) { return Number(!!(a.properties && a.properties._avora_reward_gift)) - Number(!!(b.properties && b.properties._avora_reward_gift)); });

    // Update count
    if (countEl) countEl.textContent = '(' + cart.item_count + ')';

    // Update header cart count badges
    updateHeaderCartCount(cart.item_count);

    if (items.length === 0) {
      // Empty state
      if (itemsContainer) itemsContainer.innerHTML = '';
      if (emptyState) emptyState.style.display = '';
      if (footer) footer.style.display = 'none';
      if (document.getElementById('avora-shipping-bar')) {
        document.getElementById('avora-shipping-bar').style.display = 'none';
      }
      if (window.AvoraCartUpsell) window.AvoraCartUpsell.update(cart);
      if (window.AvoraRewards) window.AvoraRewards.update(cart);
      document.dispatchEvent(new CustomEvent('avora:cart-rendered', { detail: { cart: cart } }));
      return;
    }

    // Has items
    if (emptyState) emptyState.style.display = 'none';
    if (footer) footer.style.display = '';
    if (document.getElementById('avora-shipping-bar')) {
      document.getElementById('avora-shipping-bar').style.display = '';
    }

    // Render items
    var html = '';
    items.forEach(function(item) {
      var isRewardGift = !!(item.properties && (item.properties._avora_reward_gift === true || item.properties._avora_reward_gift === 'true'));
      var imageUrl = '';
      if (item.featured_image && item.featured_image.url) {
        imageUrl = getSizedImageUrl(item.featured_image.url, '240x240');
      } else if (item.image) {
        imageUrl = getSizedImageUrl(item.image, '240x240');
      }

      var variantText = '';
      if (item.variant_title && item.variant_title !== 'Default Title') {
        variantText = item.variant_title;
      }

      var linePrice = formatCartMoney(item.final_line_price);

      html += '<div class="avora-drawer__item' + (isRewardGift ? ' is-reward-gift' : '') + (pendingQuantities[item.key] !== undefined ? ' is-updating' : '') + '" data-line-key="' + escapeAttr(item.key) + '">';
      if (imageUrl) {
        html += '  <img class="avora-drawer__item-image" src="' + escapeAttr(imageUrl) + '" alt="' + escapeAttr(item.product_title + (variantText ? ' - ' + variantText : '')) + '" loading="lazy">';
      } else {
        html += '  <div class="avora-drawer__item-image avora-drawer__item-image--placeholder" aria-hidden="true"></div>';
      }
      html += '  <div class="avora-drawer__item-details">';
      if (isRewardGift) {
        html += window.AvoraI18n.fragment('    <span class="avora-drawer__gift-badge">Gift selection</span>');
      }
      html += '    <h4 class="avora-drawer__item-title"><a href="' + escapeAttr(item.url || rootPath('products/' + item.handle)) + '">' + escapeHTML(item.product_title) + '</a></h4>';
      if (variantText) {
        html += '    <div class="avora-drawer__item-variant">' + escapeHTML(variantText) + '</div>';
      }
      html += '    <div class="avora-drawer__item-price-row">';
      html += '      <span class="avora-drawer__item-price">' + linePrice + '</span>';
      var compareLine = compareLinePrice(item);
      if (compareLine) {
        html += '      <s class="avora-drawer__item-compare">' + formatCartMoney(compareLine) + '</s>';
      }
      html += '    </div>';
      html += '  </div>';
      html += '  <div class="avora-drawer__item-actions">';
      if (!isRewardGift) {
        html += '      <div class="avora-drawer__qty">';
        html += '        <button class="avora-drawer__qty-btn" type="button" data-action="decrease"' + (item.quantity <= Math.max(1, Number(item.quantity_rule && item.quantity_rule.min) || 1) ? ' disabled' : '') + ' data-key="' + escapeAttr(item.key) + '" data-qty="' + item.quantity + window.AvoraI18n.fragment('" aria-label="Decrease quantity"><svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M5 10h10"/></svg></button>');
        html += '        <span class="avora-drawer__qty-num">' + item.quantity + '</span>';
        html += '        <button class="avora-drawer__qty-btn" data-action="increase"' + (steppedQuantity(item,item.quantity,1) === item.quantity ? ' disabled' : '') + ' data-key="' + escapeAttr(item.key) + '" data-qty="' + item.quantity + window.AvoraI18n.fragment('" aria-label="Increase quantity"><svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M5 10h10M10 5v10"/></svg></button>');
        html += '      </div>';
      } else {
        html += '      <div class="avora-drawer__qty avora-drawer__qty--gift" role="group" aria-label="Gift quantity for ' + escapeAttr(item.product_title) + '">';
        html += '        <button class="avora-drawer__qty-btn" type="button" data-avora-gift-quantity data-delta="-1"' + (item.quantity <= 1 ? ' disabled' : '') + ' data-variant-id="' + item.variant_id + '" data-line-key="' + escapeAttr(item.key) + '" data-quantity="' + item.quantity + '" aria-label="' + window.AvoraI18n.text('Decrease quantity of ') + escapeAttr(item.product_title) + window.AvoraI18n.fragment('"><svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M5 10h10"/></svg></button>');
        html += '        <span class="avora-drawer__qty-num" aria-live="polite" aria-label="Quantity ' + item.quantity + '">' + item.quantity + '</span>';
        html += '        <button class="avora-drawer__qty-btn" type="button" data-avora-gift-quantity data-delta="1" data-variant-id="' + item.variant_id + '" data-line-key="' + escapeAttr(item.key) + '" data-quantity="' + item.quantity + '" aria-label="Increase quantity of ' + escapeAttr(item.product_title) + window.AvoraI18n.fragment('"><svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M5 10h10M10 5v10"/></svg></button>');
        html += '      </div>';
      }
      html += '      <button class="avora-drawer__item-remove" data-action="remove" data-key="' + escapeAttr(item.key) + '" aria-label="Remove ' + escapeAttr(item.product_title) + '">';
      html += '        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v5m4-5v5"/></svg>';
      html += window.AvoraI18n.fragment('<span class="avora-drawer__item-remove-label">Remove</span>');
      html += '      </button>';
      html += '  </div>';
      html += '</div>';
    });

    if (itemsContainer) {
      itemsContainer.innerHTML = html;
      loadCompareAtPrices(items);

      if (mutationFocus && Object.keys(pendingQuantities).length === 0) {
        var focusItem = itemsContainer.querySelector('[data-line-key="' + mutationFocus.key + '"]');
        var focusTarget = focusItem && focusItem.querySelector('[data-action="' + mutationFocus.action + '"]');
        if (focusTarget && focusTarget.disabled) focusTarget = focusItem.querySelector('[data-action="remove"]:not(:disabled)');
        if (!focusTarget) focusTarget = itemsContainer.querySelector('[data-action]');
        if (focusTarget) window.setTimeout(function() { focusTarget.focus({ preventScroll: true }); }, 0);
        mutationFocus = null;
      }

      // Bind quantity/remove buttons via event delegation
      itemsContainer.onclick = function(e) {
        var btn = e.target.closest('[data-action]');
        if (!btn) return;

        var action = btn.dataset.action;
        var key = btn.dataset.key;
        if (btn.disabled) return;
        var qty = pendingQuantities[key] !== undefined ? pendingQuantities[key] : parseInt(btn.dataset.qty, 10);
        mutationFocus = { key: key, action: action };

        if (action === 'decrease' || action === 'increase') {
          var line = currentCart && currentCart.items.find(function(item) { return item.key === key; });
          var next = steppedQuantity(line, qty, action === 'increase' ? 1 : -1);
          if(next !== qty) changeQuantity(key, next);
        } else if (action === 'remove') {
          removeItem(key);
        }
      };
    }

    // Subtotal
    if (subtotalEl) {
      subtotalEl.textContent = formatCartMoney(cart.total_price);
    }

    // Rewards owns the journey, gift-wallet math and checkout route.
    if (window.AvoraRewards) window.AvoraRewards.update(cart);

    // Keep the gap-matched add-ons synchronized with every cart change.
    if (window.AvoraCartUpsell) window.AvoraCartUpsell.update(cart);
    document.dispatchEvent(new CustomEvent('avora:cart-rendered', { detail: { cart: cart } }));
  }

  /* ----------------------------------------------------------
     HELPERS
     ---------------------------------------------------------- */

  function formatCartMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || window.__shopCurrencyCode || 'EUR';
    if (window.AvoraMarketLocalization) return window.AvoraMarketLocalization.formatMinor(cents, currency, document.documentElement.lang || 'en', false);
    var locale = document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  function escapeHTML(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  function escapeAttr(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function getSizedImageUrl(url, size) {
    if (!url) return '';
    // Shopify CDN image resize: insert _SIZExSIZE before the extension
    return url.replace(/(\.\w+)(\?.*)?$/, '_' + size + '$1$2');
  }

  function updateHeaderCartCount(count) {
    var selectors = [
      '.cart-count-badge',
      '[data-cart-count]',
      '.cart-bubble__text-count'
    ];
    selectors.forEach(function(sel) {
      document.querySelectorAll(sel).forEach(function(el) {
        el.textContent = count;
        if (count > 0) {
          el.classList.remove('hidden');
          var bubble = el.closest('.cart-bubble');
          if (bubble) bubble.classList.remove('visually-hidden');
        } else {
          el.classList.add('hidden');
          var emptyBubble = el.closest('.cart-bubble');
          if (emptyBubble) emptyBubble.classList.add('visually-hidden');
        }
      });
    });

    document.querySelectorAll('cart-icon').forEach(function(icon) {
      icon.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
      var accessibleCount = icon.querySelector('[data-cart-count-label]');
      if (accessibleCount) {
        accessibleCount.textContent = (accessibleCount.dataset.cartCountLabel || window.AvoraI18n.text('Total items in cart')) + ': ' + count;
      }
    });

    document.querySelectorAll('[data-cart-count-label]').forEach(function(label) { label.textContent = (label.dataset.cartCountLabel || window.AvoraI18n.text('Total items in cart')) + ': ' + count; });

    try {
      sessionStorage.setItem('cart-count', JSON.stringify({ value: String(count), timestamp: Date.now() }));
    } catch (_) {}
  }

  function interceptCartIconClicks() {
    // Intercept clicks on cart icon links / buttons to open drawer instead
    document.addEventListener('click', function(e) {
      var target = e.target.closest('a[href="/cart"], a[href$="/cart"], cart-icon, .action__cart, [data-testid="cart-drawer-trigger"]');
      if (target && !target.closest('[data-avora-full-cart]') && !target.closest('.cart-page')) {
        e.preventDefault();
        e.stopPropagation();
        open();
      }
    }, true);
  }

  /* ----------------------------------------------------------
     INIT
     ---------------------------------------------------------- */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
